import React, { useCallback, useEffect, useRef, useState } from 'react';
import Image from 'next/legacy/image';
import { cloneDeep } from 'lodash';
import { Popover } from '@headlessui/react';

import iconClose from '../../public/images/close.svg';

import { usePrevious } from '../../hooks/usePrevious';

import {
  FiltersValues,
  PropertyType,
  SetFiltersValues,
} from '../../components/UnitsFilters/types';
import { defaultFilterValues } from '../../components/UnitsFilters/constants';

import PriceRange from '../../components/UnitsFilters/components/moreFiltersFields/PriceRange';
import BedroomRange from '../../components/UnitsFilters/components/moreFiltersFields/BedroomRange';
import Baths from '../../components/UnitsFilters/components/moreFiltersFields/Baths';
import Parking from '../../components/UnitsFilters/components/moreFiltersFields/Parking';
import SquareFeet from '../../components/UnitsFilters/components/moreFiltersFields/SquareFeet';
import DaysOnBlackbook from '../../components/UnitsFilters/components/moreFiltersFields/DaysOnBlackbook';
import IOnlyWant from '../../components/UnitsFilters/components/moreFiltersFields/IOnlyWant';
import SpecialTerms, {
  PageType,
} from '../../components/UnitsFilters/components/moreFiltersFields/SpecialTerms';
import Keywords from '../../components/UnitsFilters/components/moreFiltersFields/Keywords';
import PopoverSearchPage from '../../components/UnitsFilters/components/PopoverSearchPage';

import {
  getMoreFiltersLabel,
  getMoreFiltersLabelV2,
} from '../../components/Search/utils/listingsFiltersUtils';
import { useSearchCount } from '../../hooks/useSearchCount';
import { useDebounced } from 'lib/debounce';
import { isEqualValues } from 'src/utils/utils';
import { CircularProgress } from '@mui/material';
import PopoverSearchPageV2 from 'componentsV2/UnitsFiltersV2/componentsV2/PopoverSearchPageV2';
import { useOverlayFocusScroll } from 'hooks/useOverlayFocusScroll';

interface Props {
  values: FiltersValues;
  setValues: SetFiltersValues;
  buildingId: number;
  propertyType: PropertyType;
}

// Locks page scroll while the full-screen filters panel is mounted;
// cleanup restores it on any close path (X, VIEW, outside click, Escape).
const BodyScrollLock: React.FC = () => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);
  return null;
};

const BuildingPageMoreFiltersV2: React.FC<Props> = ({
  values,
  setValues,
  propertyType,
  buildingId,
}) => {
  const { count, loadListingsCount, loading } = useSearchCount();
  const [isOpen, setIsOpen] = useState(false);
  const [moreFiltersValues, setMoreFiltersValues] = useState(values);
  const [lastCalledType, setLastCalledType] = useState<PropertyType>();
  // Bumped on Reset: PriceRange/BedroomRange/SquareFeet seed local state once
  // from props and don't resync, so a remount is what visibly clears them.
  const [resetKey, setResetKey] = useState(0);

  const prevInitialValues = usePrevious(values);
  useEffect(() => {
    if (JSON.stringify(prevInitialValues) !== JSON.stringify(values)) {
      setMoreFiltersValues(values);
    }
  }, [values]);

  const [prevMoreFilterValues, setPrevMoreFilterValues] = useState({});

  const debouncedLoadListings = useDebounced<{
    filters: FiltersValues;
    buildingId: number;
    propertyType: PropertyType;
  }>(
    (val) => {
      loadListingsCount(val.filters, val.buildingId, val.propertyType);
      setLastCalledType(propertyType);
      setPrevMoreFilterValues(val.filters);
    },
    300,
    []
  );

  useEffect(() => {
    if (
      (!isEqualValues(prevMoreFilterValues, moreFiltersValues) ||
        propertyType !== lastCalledType) &&
      isOpen
    ) {
      debouncedLoadListings({
        filters: moreFiltersValues,
        buildingId,
        propertyType,
      });
    }
  }, [moreFiltersValues, isOpen, propertyType]);

  const setOpenStatus = (status: boolean) => {
    setIsOpen(status);
    return null;
  };

  const handleReset = () => {
    const cleared = cloneDeep(defaultFilterValues);
    if (propertyType === 'sale') {
      cleared.forSale = true;
      cleared.preConstruction = true;
      cleared.newConstruction = true;
      cleared.foreclosures = true;
      cleared.shortSales = true;
      cleared.existingCondos = true;
      cleared.comingSoon = true;
      cleared.forRent = false;
    }
    if (propertyType === 'rent') {
      cleared.forRent = true;
      cleared.forSale = false;
      cleared.preConstruction = false;
      cleared.newConstruction = false;
      cleared.foreclosures = false;
      cleared.shortSales = false;
      cleared.existingCondos = false;
      cleared.comingSoon = false;
    }
    setMoreFiltersValues(cleared);
    setResetKey((key) => key + 1);
  };

  // Stable ref callback: an inline one gets recreated every render, firing
  // early and committing stale values. This reads the latest via a ref.
  const moreFiltersValuesRef = useRef(moreFiltersValues);
  useEffect(() => {
    moreFiltersValuesRef.current = moreFiltersValues;
  }, [moreFiltersValues]);

  const handlePanelRef = useCallback(
    (node: HTMLElement | null) => {
      if (node === null) {
        setValues(moreFiltersValuesRef.current);
      }
    },
    [setValues]
  );

  const overlayFocusScrollRef = useOverlayFocusScroll<HTMLDivElement>();

  return (
    <PopoverSearchPageV2
      buttonClassName="border md:mb-0 md:py-2 lg:py-2.5 border-beige-dark outline-none h-[46px] border-r-0"
      name={getMoreFiltersLabelV2(
        moreFiltersValues,
        defaultFilterValues,
        'Filters'
      )}
      render={() => (
        <Popover.Panel
          className="absolute outline-none"
          ref={handlePanelRef}
        >
          {({ close, open }) => (
            <div
              ref={overlayFocusScrollRef}
              className="fixed inset-0 z-[9999] flex flex-col text-16-26-0.3 bg-white border border-gray outline-none lg:absolute lg:inset-auto lg:block lg:z-90 lg:mt-6 lg:-left-40 xl:left-0"
            >
              {setOpenStatus(open)}
              <BodyScrollLock />
              <div className="grid grid-cols-3 items-center px-4 py-3 border-b border-beige-dark shrink-0 lg:hidden">
                <button
                  className="justify-self-start uppercase text-black font-sohneKraftig font-medium text-[16px] leading-[normal] outline-none underline-gold-thin"
                  onClick={handleReset}
                >
                  Reset
                </button>
                <span
                  className="justify-self-center text-black font-sohneKraftig font-medium text-16-22-0.3"
                >
                  {getMoreFiltersLabelV2(
                    moreFiltersValues,
                    defaultFilterValues,
                    'Filters'
                  )}
                </span>
                <button
                  className="justify-self-end outline-none"
                  onClick={() => close()}
                  aria-label="Close filters"
                >
                  <Image src={iconClose} alt="close" width={20} height={20} />
                </button>
              </div>
              <div className="relative flex flex-col flex-1 min-h-[0px] outline-none lg:block lg:w-[676px]">
                <div className="hidden lg:block absolute z-90 w-6 h-6 transform rotate-45 bg-white -translate-y-1/2 lg:left-56 xl:left-12 -top-0.1 border-t border-l border-gray" />
                <div className="hidden lg:sticky lg:block top-0 h-4" />
                <div
                  key={resetKey}
                  className="flex-1 min-h-[0px] px-8 pt-4 pb-8 overflow-y-auto overscroll-contain lg:flex-none lg:h-screen-filters lg:h-auto lg:max-h-none"
                >
                  <PriceRange
                    min={moreFiltersValues.anyPrice.min}
                    max={moreFiltersValues.anyPrice.max}
                    setValues={setMoreFiltersValues}
                    page="building"
                    isBuildingPage
                  />
                  <div className="flex flex-col mb-6">
                    <div className="flex flex-col md:flex-row gap-[25px]">
                      <BedroomRange
                        min={moreFiltersValues.anyBeds.min}
                        max={moreFiltersValues.anyBeds.max}
                        setValues={setMoreFiltersValues}
                      />
                      <div className="flex md:w-1/2">
                        <Baths
                          initialValue={moreFiltersValues.baths}
                          setValues={setMoreFiltersValues}
                        />
                        <div className="self-end md:mb-[21px] md:w-[8px] px-1.5 md:px-0 mx-[7px] bg-beige border-none" />
                        <Parking
                          initialValue={moreFiltersValues.parkingPlaces}
                          setValues={setMoreFiltersValues}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col mb-6">
                    <div className="flex flex-col md:flex-row md:gap-[24px]">
                      <SquareFeet
                        min={moreFiltersValues.sqft.min}
                        max={moreFiltersValues.sqft.max}
                        setValues={setMoreFiltersValues}
                      />
                      <DaysOnBlackbook
                        setValues={setMoreFiltersValues}
                        initialValue={moreFiltersValues.daysOnCbb}
                        isBuildingPage
                      />
                    </div>
                  </div>
                  <IOnlyWant
                    values={moreFiltersValues}
                    setValues={setMoreFiltersValues}
                    page="building"
                  />
                  <SpecialTerms
                    values={moreFiltersValues}
                    setValues={setMoreFiltersValues}
                    page={PageType.building}
                  />
                  <Keywords
                    value={moreFiltersValues.keyWords}
                    setValues={setMoreFiltersValues}
                  />
                </div>
              </div>
              <div className="flex justify-center uppercase border-t border-beige-dark shrink-0">
                <button
                  disabled={loading}
                  className="items-center px-8 py-2 my-4 text-12-18-0.3 hover-animation bg-gold hover:bg-gold-hover md:text-16-26-0.3"
                  onClick={() => close()}
                >
                  VIEW{' '}
                  {loading ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    count
                  )}{' '}
                  RESULTS
                </button>
              </div>
            </div>
          )}
        </Popover.Panel>
      )}
    />
  );
};

export default BuildingPageMoreFiltersV2;
